import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Package, Pencil, Plus, Settings2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TEMPLATES } from "@/components/teacher/ActivityFields";
import { Pager, usePaged } from "@/components/teacher/ListFilter";
import { Fold } from "@/components/teacher/Collapsible";

const COPY_LABEL = { pools: "Havuzlarıma ekle", activities: "Etkinliklerime ekle", rooms: "Bu oyunu kur" };
const itemName = (kind, i) => (kind === "pools" ? i.name : kind === "activities" ? i.title : `${i.code} · ${i.pool_names?.join(", ") || ""}`);

const PackDialog = ({ kind, pack, items, onOpenChange, onSaved }) => {
  const [f, setF] = useState({ item_id: "", title: "", description: "" });
  useEffect(() => { if (pack) setF({ item_id: pack.item_id || "", title: pack.title || "", description: pack.description || "" }); }, [pack]);
  const save = async () => {
    try {
      if (pack.id) await api.put(`/packs/${pack.id}`, { title: f.title, description: f.description });
      else await api.post("/packs", { kind, ...f });
      toast.success("Paket kaydedildi");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={Boolean(pack)} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pack-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{pack?.id ? "Paketi düzenle" : "Hazır paket ekle"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!pack?.id && (
            <div className="space-y-1.5">
              <Label>İçerik</Label>
              <Select value={f.item_id} onValueChange={(v) => { const it = items.find((x) => x.id === v); setF((s) => ({ ...s, item_id: v, title: s.title || (it ? itemName(kind, it) : "") })); }}>
                <SelectTrigger className="h-11" data-testid="pack-item-select"><SelectValue placeholder="Paket yapılacak içeriği seç" /></SelectTrigger>
                <SelectContent>{items.map((i) => <SelectItem key={i.id} value={i.id}>{itemName(kind, i)}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slate-500">Seçilen içerik herkese açık yapılır. İçeriği kendi sayfasından düzenlediğinde paket de güncellenir.</p>
            </div>
          )}
          <div className="space-y-1.5"><Label>Paket adı</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="pack-title-input" /></div>
          <div className="space-y-1.5"><Label>Açıklama</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} maxLength={400} data-testid="pack-description-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={f.title.trim().length < 2 || !f.item_id} className="font-bold" data-testid="pack-save">Kaydet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PackCard = ({ kind, p, dev, onCopy, onEdit, onEditContent, onRemove }) => (
  <div className={`rounded-xl border border-dashed border-[#FACC15]/50 p-4 flex flex-col gap-2 ${p.locked ? "opacity-60" : ""}`} data-testid={`pack-${p.id}`}>
    <p className="eyebrow text-[#FACC15] truncate">{kind === "activities" && TEMPLATES[p.template] ? `${TEMPLATES[p.template].label} · ` : ""}{p.label}</p>
    <p className="font-bold">{p.title}</p>
    {p.description && <p className="text-xs text-slate-400">{p.description}</p>}
    <div className="mt-auto pt-1 flex flex-wrap gap-2">
      <Button size="sm" variant="outline" disabled={p.locked && !dev} onClick={() => onCopy(p)} data-testid={`pack-copy-${p.id}`}><Copy className="h-4 w-4 mr-1" />{COPY_LABEL[kind]}</Button>
      {dev && <Button size="sm" variant="ghost" onClick={() => onEditContent(p)} title="İçeriği düzenle" data-testid={`pack-content-${p.id}`}><Settings2 className="h-4 w-4" /></Button>}
      {dev && <Button size="sm" variant="ghost" onClick={() => onEdit(p)} title="Paket bilgilerini düzenle" data-testid={`pack-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>}
      {dev && <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onRemove(p)} title="Paketi kaldır" data-testid={`pack-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>}
    </div>
  </div>
);

export const PacksSection = ({ kind, items = [], onCopied, onEditContent }) => {
  const { teacher } = useAuth();
  const navigate = useNavigate();
  const dev = Boolean(teacher?.developer);
  const [packs, setPacks] = useState([]);
  const [editing, setEditing] = useState(null);
  const pg = usePaged(packs, 4);
  const load = useCallback(() => { api.get("/packs", { params: { kind } }).then((r) => setPacks(r.data)).catch(() => {}); }, [kind]);
  useEffect(() => { load(); }, [load]);
  const copy = async (p) => {
    try {
      const { data } = await api.post(`/packs/${p.id}/copy`);
      if (kind === "rooms") { navigate(`/ogretmen/oyun/${data.code}`); return; }
      toast.success(`"${p.title}" ${kind === "pools" ? "havuzlarınıza" : "etkinliklerinize"} eklendi`);
      onCopied?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/packs/${p.id}`); toast.success("Paket kaldırıldı"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!packs.length && !dev) return null;
  return (
    <Fold id={`packs-${kind}`} title={`Hazır paketler · ${packs.length}`} icon={<Package className="h-5 w-5 text-[#FACC15]" />}
      extra={dev && <Button size="sm" variant="outline" onClick={() => setEditing({})} data-testid={`pack-add-${kind}`}><Plus className="h-4 w-4 mr-1" />Paket ekle</Button>}>
      <div className="space-y-3" data-testid={`packs-${kind}`}>
      {packs.length === 0 && <p className="text-sm text-slate-400">Henüz hazır paket yok. "Paket ekle" ile listeden bir içerik seçin.</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {pg.items.map((p) => <PackCard key={p.id} kind={kind} p={p} dev={dev} onCopy={copy} onEdit={setEditing} onEditContent={onEditContent} onRemove={remove} />)}
      </div>
      <Pager pg={pg} testId={`packs-pager-${kind}`} />
      </div>
      <PackDialog kind={kind} pack={editing} items={items} onOpenChange={(o) => !o && setEditing(null)} onSaved={load} />
    </Fold>
  );
};
